import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Form, Button } from 'react-bootstrap';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import './buysell.css';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import { goods_categories, categoryStructure, conditionOptions, age_categories } from '../menu_options/dropdown_options';
import { cityByProvince } from '../menu_options/location_options';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useCollection } from 'react-firebase-hooks/firestore';
import { auth, db } from '../firebase';
import { FaChevronUp, FaChevronDown } from 'react-icons/fa';
import { useMediaQuery } from 'react-responsive';

const provinces = [
  'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal', 'Limpopo',
  'Mpumalanga', 'North West', 'Northern Cape', 'Western Cape'
];

const BuySell = () => {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ minWidth: 992 });
  const [isFilterExpanded, setIsFilterExpanded] = useState(isDesktop);
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search');
  const [currentBanner, setCurrentBanner] = useState(1);
  const [subcategories, setSubcategories] = useState([]);
  const [uniqueBrands, setUniqueBrands] = useState([]);
  
  useEffect(() => {
    const bannerInterval = setInterval(() => {
      setCurrentBanner((prev) => (prev % 3) + 1);
    }, 5000);

    return () => clearInterval(bannerInterval);
  }, []);

  const [filters, setFilters] = useState({
    category: '',
    subcategory: '',
    brand: '',
    condition: '',
    priceRange: [0, 100000],
    yearMin: '',
    yearMax: '',
    ageRange: '',
    province: '',
    city: ''
  });

  const [cities, setCities] = useState([]);

  useEffect(() => {
    if (filters.province) {
      setCities(cityByProvince[filters.province] || []);
      setFilters(prev => ({...prev, city: ''}));
    } else {
      setCities([]);
    }
  }, [filters.province]);

  // Update subcategories when category changes
  useEffect(() => {
    if (filters.category) {
      setSubcategories(categoryStructure[filters.category] || []);
      // Reset subcategory when category changes
      setFilters(prev => ({...prev, subcategory: ''}));
    } else {
      setSubcategories([]);
    }
  }, [filters.category]);

  const [ads, setAds] = useState([]);
  const [filteredAds, setFilteredAds] = useState([]);

  useEffect(() => {
    const fetchAds = async () => {
      const adsCollection = collection(db, 'ads');
      const adsSnapshot = await getDocs(adsCollection);
      let adsList = adsSnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      }));

      // Filter ads if search parameter exists
      const searchTerm = searchParams.get('search');
      if (searchTerm) {
        adsList = adsList.filter(ad => 
          ad.ItemName.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      // Extract unique brands from ads
      const brands = adsList
        .map(ad => ad.brand)
        .filter(brand => brand && brand.trim() !== '') // Remove empty or null brands
        .filter((brand, index, self) => self.indexOf(brand) === index) // Remove duplicates
        .sort((a, b) => a.localeCompare(b)); // Sort alphabetically

      setUniqueBrands(brands);
      setAds(adsList);
    };

    fetchAds();
  }, [searchParams]);

  useEffect(() => {
    let filteredAds = [...ads];
    
    // Apply category filter
    if (filters.category) {
      filteredAds = filteredAds.filter(ad => ad.category === filters.category);
      
      // Apply subcategory filter if available
      if (filters.subcategory) {
        filteredAds = filteredAds.filter(ad => ad.subcategory === filters.subcategory);
      }
    }
    
    // Apply brand filter
    if (filters.brand) {
      filteredAds = filteredAds.filter(ad => ad.brand === filters.brand);
    }
    
    // Apply condition filter
    if (filters.condition) {
      filteredAds = filteredAds.filter(ad => ad.condition === filters.condition);
    }
    
    // Apply price range filter
    filteredAds = filteredAds.filter(ad => {
      const price = Number(ad.Price);
      return price >= filters.priceRange[0] && price <= filters.priceRange[1];
    });
    
    // Apply year range filter if both min and max are provided
    if (filters.yearMin && filters.yearMax) {
      filteredAds = filteredAds.filter(ad => {
        const year = Number(ad.YearModel);
        return year >= Number(filters.yearMin) && year <= Number(filters.yearMax);
      });
    } 
    // Apply only min year filter
    else if (filters.yearMin) {
      filteredAds = filteredAds.filter(ad => {
        const year = Number(ad.YearModel);
        return year >= Number(filters.yearMin);
      });
    } 
    // Apply only max year filter
    else if (filters.yearMax) {
      filteredAds = filteredAds.filter(ad => {
        const year = Number(ad.YearModel);
        return year <= Number(filters.yearMax);
      });
    }
    
    // Apply age range filter
    if (filters.ageRange) {
      filteredAds = filteredAds.filter(ad => ad.ageRange === filters.ageRange);
    }

    // Apply province filter
    if (filters.province) {
      filteredAds = filteredAds.filter(ad => ad.province === filters.province);
      
      // Apply city filter if available
      if (filters.city) {
        filteredAds = filteredAds.filter(ad => ad.city === filters.city);
      }
    }
    
    setFilteredAds(filteredAds);
  }, [filters, ads]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prevFilters => ({ ...prevFilters, [filterName]: value }));
  };

  const toggleFilterColumn = () => {
    setIsFilterExpanded(!isFilterExpanded);
  };

  useEffect(() => {
    setIsFilterExpanded(isDesktop);
  }, [isDesktop]);

  useEffect(() => {
    console.log(`Filtered ads: ${filteredAds.length}`);
    console.log('Current filters:', filters);
    console.log('Ads data:', filteredAds);
  }, [filteredAds, filters]);

  const formatPrice = (price) => {
    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  };

  return (
    <Container fluid className='main-buysell'>
      <Row className="m-0">
        <Col className="ad-banner">
          <img 
            src={`/Banner${currentBanner}.svg`}
            alt={`Totly Store Banner ${currentBanner}`}
            className="img-fluid w-100"
            style={{ transition: 'opacity 0.5s ease-in-out' }}
          />
        </Col>
      </Row>
      <Row className="button-row align-items-center">
        <Col xs={12} className="d-flex justify-content-left">
          <button className="button-custom button-create me-2" onClick={() => navigate('/create-ad')}>Create Ad</button>
          <button className="button-custom button-mylistings" onClick={() => navigate('/my-listings')}>My Listings</button>
        </Col>
      </Row>
      <Row className="m-0">
        <Col md={3} className="filter-col">
          <div className="filter-header d-flex justify-content-between align-items-center">
            <h4>Filters</h4>
            <button 
              onClick={toggleFilterColumn} 
              className="toggle-filter-btn d-md-none"
              aria-label={isFilterExpanded ? "Collapse filters" : "Expand filters"}
            >
              {isFilterExpanded ? <FaChevronUp /> : <FaChevronDown />}
            </button>
          </div>
          <div className={`filter-content ${isFilterExpanded ? 'expanded' : 'collapsed'}`}>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Category</Form.Label>
                <Form.Select 
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  value={filters.category}
                >
                  <option value="">All Categories</option>
                  {goods_categories.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              
              {filters.category && (
                <div className="subcategory-filter">
                  <Form.Group>
                    <Form.Label>Subcategory</Form.Label>
                    <Form.Select 
                      onChange={(e) => handleFilterChange('subcategory', e.target.value)}
                      value={filters.subcategory}
                    >
                      <option value="">All Subcategories</option>
                      {subcategories.map((subcategory) => (
                        <option key={subcategory} value={subcategory}>{subcategory}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
              )}

              {uniqueBrands.length > 0 && (
                <Form.Group className="mb-3">
                  <Form.Label>Brand</Form.Label>
                  <Form.Select 
                    onChange={(e) => handleFilterChange('brand', e.target.value)}
                    value={filters.brand}
                  >
                    <option value="">All Brands</option>
                    {uniqueBrands.map((brand) => (
                      <option key={brand} value={brand}>{brand}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Province</Form.Label>
                <Form.Select 
                  onChange={(e) => handleFilterChange('province', e.target.value)}
                  value={filters.province}
                >
                  <option value="">All Provinces</option>
                  {provinces.map((province) => (
                    <option key={province} value={province}>{province}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              {filters.province && (
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Select 
                    onChange={(e) => handleFilterChange('city', e.target.value)}
                    value={filters.city}
                  >
                    <option value="">All Cities</option>
                    {cities.map((city) => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}
              <Form.Group className="mb-3">
                <Form.Label>Condition</Form.Label>
                <Form.Select 
                  onChange={(e) => handleFilterChange('condition', e.target.value)}
                  value={filters.condition}
                >
                  <option value="">All Conditions</option>
                  {conditionOptions.map((condition) => (
                    <option key={condition} value={condition}>{condition}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Price Range: R{filters.priceRange[0]} - R{filters.priceRange[1]}</Form.Label>
                <Slider
                  range
                  min={0}
                  max={100000}
                  step={1000}
                  value={filters.priceRange}
                  onChange={(value) => handleFilterChange('priceRange', value)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Age Range</Form.Label>
                <Form.Select 
                  onChange={(e) => handleFilterChange('ageRange', e.target.value)}
                  value={filters.ageRange}
                >
                  <option value="">All Age Ranges</option>
                  {age_categories.map((age) => (
                    <option key={age} value={age}>{age}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Form>
          </div>
        </Col>
        <Col md={9} className="listings-col">
          <Row xs={2} sm={2} md={3} lg={4} xl={5} className="g-3">
            {filteredAds.map((ad) => (
              <Col key={ad.id}>
                <Link to={`/view-ad/${ad.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <Card className="ad-card h-100">
                    <div style={{ position: 'relative' }}>
                      <Card.Img
                        variant="top"
                        src={ad.imageUrls?.[0] || ad.imageUrl || ad.images?.[0] || '/placeholder-image.jpg'}
                        className="card-img-top"
                      />
                      {(ad.imageUrls?.length > 1 || ad.images?.length > 1) && (
                        <Badge bg="secondary" style={{
                          position: 'absolute',
                          top: '5px',
                          right: '5px',
                          padding: '3px 6px',
                          borderRadius: '50%',
                          fontSize: '0.8rem'
                        }}>
                          {ad.imageUrls?.length || ad.images?.length}
                        </Badge>
                      )}
                    </div>
                    <Card.Body className="d-flex flex-column p-2">
                      <Card.Title className="mb-1 fs-6">{ad.title || ad.ItemName || 'No Title'}</Card.Title>
                      {ad.YearModel && <Card.Text className="mb-0 small">Year: {ad.YearModel}</Card.Text>}
                      {ad.Price && <Card.Text className="mb-1 small">Price: R{formatPrice(ad.Price)}</Card.Text>}
                      <Card.Text className="mb-1 small flex-grow-1" style={{ color: '#888888' }}>
                        {ad.description 
                          ? ad.description.split(' ').slice(0, 10).join(' ') + 
                            (ad.description.split(' ').length > 10 ? '...' : '')
                          : 'No description'}
                      </Card.Text>
                      <Card.Text className="mt-auto text-muted small">
                        {ad.city && ad.province 
                          ? `${ad.city}, ${ad.province}`
                          : ad.province || 'Location not specified'}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Link>
              </Col>
            ))}
          </Row>
        </Col>
        <Col md={3} className="blank-col">
          {/* This column is intentionally left blank */}
        </Col>
      </Row>
    </Container>
  );
};

export default BuySell;